import { Component, Inject, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Subject, Observable, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import * as shape from 'd3-shape';
import { PackageDependenciesGraph } from './package-dependencies-graph';
import { PackageDependenciesService } from './package-dependencies.service';
import { BZG_MODAL_DATA, BzgModalRef } from 'bz-genome-angular';
import { PACKAGE_VISUALIZER_DATA } from '../core/contracts/package-visualizer.data';
import { Store, select } from '@ngrx/store';
import { getPackageDependencies, getIsLoading } from '../store/selectors/package-visualizer.selectors';
import { GetDependencies } from '../store/actions/package-visualizer.actions';
import { IAppState } from 'app/IAppState';
import { colorSets } from '@swimlane/ngx-charts/release/utils';
import { DomSanitizer } from '@angular/platform-browser';
import * as DOMPurify from 'dompurify'

@Component({
  selector: 'app-package-dependencies',
  templateUrl: './package-dependencies.component.html',
  styleUrls: ['./package-dependencies.component.scss']
})
export class PackageDependenciesComponent implements OnInit {
  objectGuid: Guid;
  objectName: string;
  icon: any;
  packageDependencies: IPackageDependencies;
  selectedPath: string;
  paths = [];
  idBrowser: Guid;
  noresults = false;
  view: any[];
  width = 1150;
  height = 600;
  fitContainer = true;
  autoZoom = true;
  panOnZoom = true;
  enableZoom = true;
  autoCenter = true;
  hierarchialGraph: PackageDependenciesGraph; // =  new PackageDependenciesGraph();
  links: any;
  nodes: any;
  context: string;
  zoomLevel = 0.1;

  // observables
  update$: Subject<any> = new Subject();
  center$: Subject<any> = new Subject();
  zoomToFit$: Subject<any> = new Subject();

  // options
  showLegend = false;
  orientation = 'LR';
  curveType = 'Linear';
  curve: any = shape.curveLinear;
  colorSchemes: any;
  colorScheme: any;
  schemeType = 'ordinal';
  selectedColorScheme: string;

  public packageDependencies$: Observable<any>;
  public packageDependenciesSubscription: Subscription;
  public isLoading$: Observable<boolean>;
  private _catalogObjectId: string;

  constructor(@Inject(BZG_MODAL_DATA) public data: { catalogObjectId: string },
    private _store: Store<IAppState>,
    private location: Location,
    private _route: ActivatedRoute,
    private _packageDependenciesService: PackageDependenciesService,
    public modalComponentRef: BzgModalRef,
    private sanitizer: DomSanitizer) {
    this._catalogObjectId = data.catalogObjectId;
    this.packageDependencies$ = this._store.pipe(select(getPackageDependencies));
    this.isLoading$ = this._store.pipe(select(getIsLoading));


    Object.assign(this, {
      colorSchemes: colorSets
    });

    this.setColorScheme('picnic');
    this.setInterpolationType('Linear');
  }

  ngOnInit() {
    this._route.params.subscribe((params) => {
      this.objectGuid = params['id'];
      this.objectName = params['name'];
      if (this.objectName)
        this.objectName = this.sanitizeText(this.objectName);
      this.initializeTraceDependenciesInGraph();
      this.center();
    });

    this._route.queryParams.subscribe((queryParams) => {
      this.idBrowser = queryParams['browserguid'];
    });

    this.packageDependenciesSubscription = this.packageDependencies$.subscribe(
      dependencies => {
        this.packageDependencies = dependencies;
        this.initializeTraceDependenciesInGraph();
      }
    );

    this._store.dispatch(new GetDependencies(this._catalogObjectId));
  }

  ngOnDestroy(): void {
    if (this.packageDependenciesSubscription) {
      this.packageDependenciesSubscription.unsubscribe();
    }
  }

  updateData() {
    const target = this.packageDependencies.target;
    const paths = this.packageDependencies.paths;
    const path = paths.find((element) => {
      return element.displayName === this.selectedPath;
    });

    this.hierarchialGraph = this._packageDependenciesService.buildGraphContainer(target, path);
    this.hierarchialGraph.links.forEach(link => {
      link.label = this.sanitizeText(link.label);
    });
    this.hierarchialGraph.nodes.forEach(node => {
      node.label = this.sanitizeText(node.label);
    });
  }

  sanitizeText(text: string): string {
    return DOMPurify.sanitize(text, {
      USE_PROFILES: { html: false }
    });
  }

  applyDimensions() {
    this.view = [this.width, this.height];
  }

  toggleEnableZoom(enableZoom: boolean) {
    this.enableZoom = enableZoom;
  }

  toggleFitContainer(fitContainer: boolean, autoZoom: boolean, autoCenter: boolean): void {
    this.fitContainer = fitContainer;
    this.autoZoom = autoZoom;
    this.autoCenter = autoCenter;

    if (this.fitContainer) {
      this.view = undefined;
    } else {
      this.applyDimensions();
    }
  }

  select(data) {
  }

  setColorScheme(name) {
    this.selectedColorScheme = name;
    this.colorScheme = this.colorSchemes.find(s => s.name === name);
  }

  setInterpolationType(curveType) {
    this.curveType = curveType;
    if (curveType === 'Bundle') {
      this.curve = shape.curveBundle.beta(1);
    }
    if (curveType === 'Cardinal') {
      this.curve = shape.curveCardinal;
    }
    if (curveType === 'Catmull Rom') {
      this.curve = shape.curveCatmullRom;
    }
    if (curveType === 'Linear') {
      this.curve = shape.curveLinear;
    }
    if (curveType === 'Monotone X') {
      this.curve = shape.curveMonotoneX;
    }
    if (curveType === 'Monotone Y') {
      this.curve = shape.curveMonotoneY;
    }
    if (curveType === 'Natural') {
      this.curve = shape.curveNatural;
    }
    if (curveType === 'Step') {
      this.curve = shape.curveStep;
    }
    if (curveType === 'Step After') {
      this.curve = shape.curveStepAfter;
    }
    if (curveType === 'Step Before') {
      this.curve = shape.curveStepBefore;
    }
  }

  onLegendLabelClick(entry) {
  }

  toggleExpand(node) {
  }

  updateChart() {
    this.update$.next(true);
  }

  zoomToFit() {
    this.zoomToFit$.next(true);
    this.center();
  }

  setZoomLevel(zoomLevel: number) {
    this.zoomLevel = 1;
    this.center();
  }

  center() {
    this.center$.next(true);
  }

  goBack() {
    this.location.back();
  }

  onOk() {
    const refresh: IRefresh = {} as IRefresh;

    refresh.refresh = false;
    refresh.guidObject = null;
    refresh.browserGuid = this.idBrowser;
    // this._commonServices.closeWindow();
  }

  openBizagiHelp() {
    // this._commonServices.openBizagiHelp();
  }

  pathChanged($event) {
    this.selectedPath = $event;
    this.updateData();
  }

  close() {
    this.modalComponentRef.close();
  }

  private initializeTraceDependenciesInGraph() {
    if (this.packageDependencies === undefined ||
      this.packageDependencies === null) {
      this.noresults = true;
      return;
    }
    this.noresults = false;
    this.getObjectTitle();
    this.getPaths();
    this.selectedPath = this.packageDependencies.paths.length > 0 ? this.packageDependencies.paths[0].displayName : 'Not found';
    this.updateData();
    // }).catch(() => {
    //   if (isRejected) {
    //     this.noresults = true;
    //   }
    //  });
  }

  private getPaths() {
    this.paths = [];
    this.packageDependencies.paths.forEach(path => {
      this.paths.push(path.displayName);
    });
  }

  private getObjectTitle() {
    this.objectName = this.packageDependencies.target.objectData.displayName + ' (' +
      this.packageDependencies.target.objectData.type + ')';
    if (this.objectName)
      this.objectName = this.sanitizeText(this.objectName);
    // this.icon = this._packageService.getImageMap()[this.packageDependencies.target.objectData.type.toLowerCase()]
    //     || this.packageDependencies.target.objectData.type.toLowerCase();
  }
}
